import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { Section } from '../api/types'
import { useTodos, useUpdateTodo, useDeleteSection } from '../api/hooks'
import { TodoItem } from './TodoItem'
import { TodoInput } from './TodoInput'
import { between, atEnd } from '../lib/fracidx'
import { sortTodos, computeSnappedPosition } from '../lib/todoSort'
import { useUIStore } from '../store/ui'
import { useToast } from './primitives/Toast'

interface SectionViewProps {
  section: Section
}

export function SectionView({ section }: SectionViewProps) {
  const { data: todos = [], isLoading } = useTodos({ section_id: section.id })
  const updateTodo = useUpdateTodo()
  const deleteSection = useDeleteSection()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const handleDelete = useCallback(() => {
    if (!window.confirm(`Delete section "${section.name}"?`)) return
    deleteSection.mutate(section.id, {
      onSuccess: () => navigate('/week'),
      onError: () => showToast('Failed to delete section', 'error'),
    })
  }, [section.id, section.name, deleteSection, navigate, showToast])

  const smartStatusSort = useUIStore((s) => s.smartStatusSort)
  const sortedTodos = useMemo(
    () => sortTodos(todos, smartStatusSort),
    [todos, smartStatusSort]
  )
  const todoIds = sortedTodos.map((t) => t.id)

  // Track which item is being dragged so DragOverlay can render the preview.
  const [activeId, setActiveId] = useState<string | null>(null)
  const activeTodo = useMemo(
    () => (activeId ? todos.find((t) => t.id === activeId) ?? null : null),
    [activeId, todos]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      const { active, over } = event
      if (!over || active.id === over.id) return

      const draggedId = active.id as string
      const overId = over.id as string

      let newPosition: number
      if (smartStatusSort) {
        const dragged = todos.find((t) => t.id === draggedId)
        if (!dragged) return

        // Direction-aware beforeId: same logic as WeeklyGrid's handleDragOver.
        const activeIdx = sortedTodos.findIndex((t) => t.id === draggedId)
        const overIdx = sortedTodos.findIndex((t) => t.id === overId)
        let beforeId: string | null
        if (activeIdx !== -1 && overIdx !== -1 && activeIdx < overIdx) {
          // Dragging down — insert after over → beforeId = next item or null.
          beforeId = sortedTodos[overIdx + 1]?.id ?? null
        } else {
          // Dragging up or cross — insert before over.
          beforeId = overId
        }

        const targetCol = sortTodos(todos.filter((t) => t.id !== draggedId), true)
        newPosition = computeSnappedPosition(targetCol, dragged.status, beforeId)
      } else {
        // Sorted list excluding the dragged item so position math is clean
        const items = sortedTodos.filter((t) => t.id !== draggedId)
        const overIdx = items.findIndex((t) => t.id === overId)

        if (overIdx <= 0) {
          newPosition = items.length > 0 ? items[0].position - 1 : 1
        } else if (overIdx >= items.length - 1) {
          newPosition = atEnd(items[items.length - 1].position)
        } else {
          newPosition = between(items[overIdx - 1].position, items[overIdx].position)
        }
      }

      updateTodo.mutate({ id: draggedId, position: newPosition })
    },
    [todos, sortedTodos, smartStatusSort, updateTodo]
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '64px 32px 48px' }}>
      <header style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h1 className="display-serif" style={{ marginBottom: '6px', flex: 1, minWidth: 0 }}>
            {section.name}
          </h1>
          <button
            onClick={handleDelete}
            aria-label="Delete section"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-ink-faint)',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Trash2 size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="eyebrow">
          {todos.length} item{todos.length !== 1 ? 's' : ''}
        </div>
      </header>

      {isLoading ? (
        <div
          style={{
            color: 'var(--color-ink-faint)',
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
          }}
        >
          loading…
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={todoIds} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {sortedTodos.map((todo) => (
                <TodoItem key={todo.id} todo={todo} />
              ))}
            </div>
          </SortableContext>

          {todos.length === 0 && (
            <div
              style={{
                color: 'var(--color-ink-faint)',
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: '0.9375rem',
                padding: '16px 0',
              }}
            >
              No tasks yet. Add one below.
            </div>
          )}

          <div style={{ marginTop: '24px' }}>
            <TodoInput
              defaultSectionId={section.id}
              todos={todos}
              placeholder="+ add"
            />
          </div>

          {/* Floating drag preview — follows cursor during drag */}
          <DragOverlay>
            {activeTodo && (
              <TodoItem todo={activeTodo} isOverlay />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
